import React, { useRef } from 'react'
import { TextStyle, useWindowDimensions } from 'react-native'
import { observer } from 'mobx-react-lite'
import { Instance } from 'mobx-state-tree'
import { useMediaQuery } from '@mui/material'

import { FeedbackModal } from '@trivie/ui-mobile'
import { Text } from '@trivie/ui-mobile/components/text'
import { Branding } from '@trivie/core/src/models/Branding'
import { GameQuestion } from '@trivie/core/src/models/game/GameQuestion'

import { Choices } from './ChoicesList'
import { MatchingChoices } from './MatchingChoices'
import { OrderedChoices } from './OrderedChoices'
import { OpenTextChoice } from './OpenTextChoice'
import { ScaleChoice } from './ScaleChoice'
import { QuestionImage } from '../question-image'
import GradientScroll from 'react-gradient-scroll-indicator'

interface QuestionProps {
  branding: Instance<typeof Branding>
  // Accepts a game GameQuestion or a survey SurveyPlayQuestion (same read surface).
  question: Instance<typeof GameQuestion> | any
  /** 'survey' disables scoring/feedback. Defaults to 'quiz'. */
  mode?: 'quiz' | 'survey'
}
export const Question = observer((props: QuestionProps) => {
  const { question, branding, mode = 'quiz' } = props
  const { text_color } = branding
  const {
    text,
    image_url,
    choices,
    showFeedback,
    selected,
    answeredCorrectly,
    showChoiceFeedback,
    continueFromFeedback,
  } = question

  const feedbackRef = useRef<any>(null)

  const { width } = useWindowDimensions()
  const isMobile = !useMediaQuery('(min-width:800px)')

  const renderChoices = () => {
    switch (question.question_type) {
      case 'Matching':
        return <MatchingChoices question={question} branding={branding} />
      case 'Ordering':
        return <OrderedChoices question={question} branding={branding} />
      case 'Open Text':
        return <OpenTextChoice mode={mode} question={question} branding={branding} />
      case 'Scale':
        return <ScaleChoice question={question} branding={branding} />
      default:
        return (
          <Choices
            style={{ paddingTop: 0 }}
            mode={mode}
            question={question}
            branding={branding}
            choices={choices}
          />
        )
    }
  }

  return (
    <>
      <GradientScroll primaryColor={branding.quiz_background} fadeHeight={'20px'}>
        <div style={CONTAINER}>
          <QuestionImage
            imgStyle={{ minHeight: 300, maxHeight: 300, maxWidth: width - 40, alignSelf: 'center' }}
            url={question.image_url}
          />
          <Text
            align="left"
            text={text}
            disableLink
            variant="labelEmphasized"
            style={{ ...STEM, fontSize: isMobile ? 16 : 20, color: text_color }}
          />
          {renderChoices()}
        </div>
        {mode !== 'survey' && (
          <FeedbackModal
            visible={showFeedback}
            question={question}
            selection={selected}
            showChoiceFeedback={showChoiceFeedback}
            correct={answeredCorrectly}
            feedbackRef={feedbackRef}
            onContinue={continueFromFeedback}
            branding={branding}
          />
        )}
      </GradientScroll>
    </>
  )
})

const CONTAINER: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  padding: 20,
  height: '100%',
}
const STEM = {
  flex: 1,
  fontSize: 20,
  lineHeight: 32,
  maxWidth: 1000,
  justifyContent: 'center',
  alignSelf: 'center',
  alignItems: 'center',
  display: 'flex',
  width: '100%',
} as TextStyle
